import React, { useState, useEffect } from "react";
import { auth, googleProvider } from "../../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  OAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import { IoLogoApple } from "react-icons/io5";
import { getFriendlyFirebaseSignupErrorMessage } from "../../utils/auth";
import { createUnverifiedUser, socialSignUp } from "../../utils/utils";
import { getOnboardingRedirectPath } from "../../utils/onboardingNavigation";
import { useUser } from "../../context/UserContext";
import { FiEye, FiEyeOff, FiCheckCircle } from "react-icons/fi";

const SESSION_KEY = "signupVerificationNotice";
const SESSION_EMAIL_KEY = "signupVerificationEmail";

const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(SESSION_KEY) === "true";
  });
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [setupWarning, setSetupWarning] = useState("");

  const navigate = useNavigate();
  const { loadUserContext } = useUser();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (verificationSent) {
      sessionStorage.setItem(SESSION_KEY, "true");
    } else {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_EMAIL_KEY);
    }
  }, [verificationSent]);

  const handleRedirectToLogin = (withNotice = false) => {
    if (withNotice) {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_EMAIL_KEY);
      setVerificationSent(false);
      navigate("/auth/login", {
        replace: true,
        state: { verificationSent: true },
      });
      return;
    }
    navigate("/auth/login", { replace: true });
  };

  const handleResendVerification = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setResendStatus("Still finishing signup—wait a moment and try again.");
      return;
    }

    setResending(true);
    setResendStatus("");
    try {
      await sendEmailVerification(currentUser);
      setResendStatus("Verification email sent again. Check spam just in case!");
    } catch {
      setResendStatus("Could not resend verification right now. Please try again shortly.");
    } finally {
      setResending(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword((p) => !p);

  const isPasswordSecure = (pwd) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&^()\-_=+]).{8,}$/.test(pwd);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    setPasswordError("");

    if (!firstName.trim() || !lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (!isPasswordSecure(password)) {
      setPasswordError("Password must be ≥8 chars, include upper, lower, number & special.");
      return;
    }
    if (!confirmedAge) {
      setError("You must confirm you are at least 18 years old.");
      return;
    }

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      try {
        await sendEmailVerification(user);
      } catch (emailErr) {
        setError(
          emailErr?.message ||
            "Your account was created, but we couldn't send the verification email. Try again from the login page or contact support."
        );
        setLoading(false);
        return;
      }

      // The Firebase account now exists, so a backend hiccup must not send the
      // user back to a form that will only ever say "email already in use".
      // Every authenticated endpoint backfills the record, so surface a warning
      // and let them continue.
      try {
        await createUnverifiedUser({
          confirmed_over_18: true,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        });
      } catch (backendErr) {
        console.error("Backend user creation failed during signup:", backendErr);
        setSetupWarning(
          "Your account was created, but we couldn't finish setting up your profile. Verify your email and log in — we'll finish it then."
        );
      }

      sessionStorage.setItem(SESSION_KEY, "true");
      sessionStorage.setItem(SESSION_EMAIL_KEY, email);
      setVerificationSent(true);
    } catch (err) {
      setError(getFriendlyFirebaseSignupErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const extractName = (user) => {
    const displayName = user.displayName || "";
    const [first, ...rest] = displayName.split(" ");
    return { first_name: first || "", last_name: rest.join(" ") || "" };
  };

  // Social "sign up" and social "log in" are the same OAuth handshake, so a
  // returning user can land here. Route on what their account already has
  // rather than assuming everyone needs onboarding.
  const finishSocialSignup = async (firebaseUser) => {
    await socialSignUp(confirmedAge, extractName(firebaseUser));

    let ctx = null;
    try {
      ctx = await loadUserContext();
    } catch (ctxErr) {
      console.error("Could not load context after social signup:", ctxErr);
    }

    const destination = getOnboardingRedirectPath(ctx || null);
    navigate(destination, {
      replace: true,
      state: destination === "/setup-representative" ? { fromSignup: true } : undefined,
    });
  };

  const handleGoogleSignup = async () => {
    if (!confirmedAge) {
      setError("You must confirm you are at least 18 years old.");
      return;
    }
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await new Promise(resolve => setTimeout(resolve, 100));
      await result.user.getIdToken(true);
      await finishSocialSignup(result.user);
    } catch (err) {
      setError(getFriendlyFirebaseSignupErrorMessage(err, "Google"));
    }
  };

  const handleAppleSignup = async () => {
    if (!confirmedAge) {
      setError("You must confirm you are at least 18 years old.");
      return;
    }
    const provider = new OAuthProvider("apple.com");
    try {
      const result = await signInWithPopup(auth, provider);
      await new Promise(resolve => setTimeout(resolve, 100));
      await result.user.getIdToken(true);
      await finishSocialSignup(result.user);
    } catch (err) {
      setError(getFriendlyFirebaseSignupErrorMessage(err, "Apple"));
    }
  };

  if (verificationSent) {
    const displayEmail =
      email ||
      auth.currentUser?.email ||
      (typeof window !== "undefined" ? sessionStorage.getItem(SESSION_EMAIL_KEY) : "") ||
      "";
    // Note: fallback chain exists to handle the restore-from-sessionStorage path
    // (when this component re-mounts after a navigation and email state is empty).

    return (
      <div className="w-full space-y-5 text-center">
        <FiCheckCircle className="mx-auto h-10 w-10 text-[#047857]" aria-hidden />
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight text-[#0f172a]">Verification email sent</h2>
          <p className="text-sm text-[#334155]">
            We just sent a verification link to{" "}
            {displayEmail ? (
              <span className="font-medium text-[#0f172a]">{displayEmail}</span>
            ) : (
              "your email"
            )}
            . Click the link in your inbox (or spam folder) to finish creating your account.
          </p>
        </div>
        {setupWarning && (
          <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs text-amber-900">
            {setupWarning}
          </div>
        )}
        <div className="flex w-full flex-col gap-2">
          <button
            type="button"
            onClick={() => handleRedirectToLogin(true)}
            className="inline-flex w-full items-center justify-center rounded-full bg-[#047857] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#065f46] focus:outline-none focus:ring-4 focus:ring-[#047857]/30"
          >
            Go to Login
          </button>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resending}
            className="inline-flex w-full items-center justify-center rounded-full border border-[#cbd5e1] bg-white px-4 py-2 text-sm font-semibold text-[#047857] transition hover:bg-[#f8fafc] focus:outline-none focus:ring-4 focus:ring-[#047857]/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resending ? "Sending..." : "Resend email"}
          </button>
        </div>
        {resendStatus && (
          <p className="text-xs text-[#334155]">{resendStatus}</p>
        )}
        <p className="text-xs text-[#64748b]">
          Need help? Forward the verification email to{" "}
          <a href="mailto:support@civixthread.com" className="font-medium text-[#047857] underline">
            support@civixthread.com
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-[#0f172a]">Create an account.</h1>
        <p className="mt-2 text-sm italic text-[#0f172a]">Your voice matters. Let&apos;s amplify it.</p>
      </div>

      <form onSubmit={handleSignup} className="mt-6 space-y-4">
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col">
            <label htmlFor="signup-first" className="text-xs font-medium text-[#0f172a]">First Name</label>
            <input
              id="signup-first"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
              placeholder="First name"
              className="mt-1 block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />
          </div>
          <div className="flex flex-1 flex-col">
            <label htmlFor="signup-last" className="text-xs font-medium text-[#0f172a]">Last Name</label>
            <input
              id="signup-last"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
              placeholder="Last name"
              className="mt-1 block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="signup-email" className="text-xs font-medium text-[#0f172a]">Email Address</label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            placeholder="you@example.com"
            className="mt-1 block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="signup-password" className="text-xs font-medium text-[#0f172a]">Password</label>
          <div className="mt-1 relative">
            <input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              placeholder="Create a password"
              className="block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 pr-11 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-black hover:text-slate-700"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="signup-confirm" className="text-xs font-medium text-[#0f172a]">Confirm Password</label>
          <div className="mt-1 relative">
            <input
              id="signup-confirm"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              placeholder="Confirm your password"
              className="block w-full rounded-xl border border-[#cbd5e1] bg-white px-3 py-2 pr-11 text-base text-[#0f172a] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#047857]/30 focus:border-[#047857]"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-black hover:text-slate-700"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>

        {passwordError && (
          <p className="text-xs text-[#dc2626]">{passwordError}</p>
        )}

        <p className="text-xs text-[#0f172a]">Use at least 8 characters with a mix of letters, numbers & symbols</p>

        <label className="my-3 flex items-start gap-2 text-sm text-[#0f172a] cursor-pointer">
          <input
            type="checkbox"
            checked={confirmedAge}
            onChange={(e) => setConfirmedAge(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[#cbd5e1] text-[#047857] focus:ring-[#047857]/30"
          />
          <span>I confirm I am at least 18 years old</span>
        </label>

        <button
          type="submit"
          disabled={loading || !confirmedAge}
          className="mt-1 inline-flex w-full items-center justify-center rounded-full bg-[#047857] px-5 py-3.5 text-base font-semibold text-white transition hover:bg-[#065f46] focus:outline-none focus:ring-4 focus:ring-[#047857]/30 disabled:opacity-60"
        >
          {loading ? (
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            "Sign Up"
          )}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-[#e2e8f0]" />
        <span className="px-2 text-xs font-medium uppercase tracking-wider text-[#64748b]">or</span>
        <span className="h-px flex-1 bg-[#e2e8f0]" />
      </div>

      <p className="text-center text-sm text-[#0f172a]">Sign up with your preferred account</p>

      <div className="mt-4 flex justify-center gap-4">
        <button
          onClick={handleGoogleSignup}
          disabled={!confirmedAge}
          aria-label="Sign up with Google"
          className="social-btn"
          style={!confirmedAge ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          <FaGoogle />
        </button>
        <button
          onClick={handleAppleSignup}
          disabled={!confirmedAge}
          aria-label="Sign up with Apple"
          className="social-btn"
          style={!confirmedAge ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          <IoLogoApple />
        </button>
      </div>
    </div>
  );
};

export default Signup;
